import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { FcGoogle } from "react-icons/fc";
import { useNavigate, useParams } from "react-router-dom";
import { useGetEvent } from "../../api/queries/useGetEvent";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import {
  useMakePayment,
  type CreatePaymentIntentResponse,
} from "../../api/mutations/useMakePayment";
import { previewWalletPricing } from "../../api/mutations/usePreviewWalletPricing";
import { runWalletPaymentRequest } from "../../utils/stripeExpressWalletPay";
import { useBookEvent } from "../../api/mutations/useBookEvent";
import { useSendEventBookingConfirmation } from "../../api/mutations/useSendEventBookingConfirmation";
import {
  usePreviewEventStripeDiscount,
  type PreviewEventStripeDiscountResponse,
} from "../../api/mutations/usePreviewEventStripeDiscount";
import { useQueryClient } from "@tanstack/react-query";
import { useGetBilling } from "../../api/queries/useGetBilling";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { getSavedCards } from "../../utils/savedPaymentMethods";
import { useAppContext } from "../../Context/AppContext";
import {
  logPaymentError,
  getPaymentErrorSource,
  getPaymentErrorMessage,
} from "../../utils/paymentErrorLogger";
import {
  getUnsupportedWalletMessage,
  getWalletButtonVisibility,
} from "../../utils/walletSupport";
import {
  EVENT_DATA_KEY,
  resolveEventBookingFormData,
} from "../../explorer/resolveEventBookingFormData";
import { ensureExplorerProfileSyncedForEventBooking } from "../../explorer/explorerProfileSync";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import { useTranslation } from "react-i18next";
import {
  canUserViewEvent,
  isMembershipAccessCheckPending,
} from "../../utils/eventMembershipVisibility";
import {
  eventEligibleForMemberTierDiscount,
  MEMBERSHIP_BUSINESS,
  MEMBERSHIP_EXECUTIVE,
  userQualifiesForMemberEventDiscount,
} from "../../utils/eventMemberDiscount";
import { isEventAdminFreeForUser } from "../../utils/eventFreeForGroups";

const EventSuccessModal = lazy(() => import("./EventSuccessModal"));

const BACK_ROUTE = "/event-booking";

const STRIPE_ELEMENT_STYLE = {
  base: {
    fontSize: "16px",
    color: "#000",
    "::placeholder": { color: "#999" },
  },
  invalid: { color: "red" },
} as const;

interface PriceSummaryProps {
  data: { price?: number; earlyBirdPrice?: number } | undefined;
  isEarlyBird: boolean;
  finalPrice: string;
  membershipName: string | undefined;
  /** Subtotal after early bird, before Business/Executive member pricing */
  subtotalBeforeMemberDiscount: string;
  currencyCode: string | null | undefined;
  /** Amount taken off by Stripe event promotion (after early bird, before member tier). */
  eventCouponDiscountAmount?: number | null;
  /** When set, used as the “before member discount” baseline so the member line matches promo + tier. */
  ticketSubtotalAfterPromoForMemberLine?: string | null;
  memberDiscountEligible?: boolean;
  /**
   * Complimentary ticket (admin free-for-groups or Executive first-in-month).
   * Show a simple £0 total — no discount line-item theatre.
   */
  isComplimentaryTicket?: boolean;
}

const PriceSummary = memo(function PriceSummary({
  data,
  isEarlyBird,
  finalPrice,
  membershipName,
  subtotalBeforeMemberDiscount,
  currencyCode,
  eventCouponDiscountAmount = null,
  ticketSubtotalAfterPromoForMemberLine = null,
  memberDiscountEligible = true,
  isComplimentaryTicket = false,
}: PriceSummaryProps) {
  const { t } = useTranslation();
  const fmt = (n: number | null | undefined) =>
    formatCurrencyAmount(n ?? 0, currencyCode);

  if (isComplimentaryTicket || parseFloat(finalPrice) <= 0) {
    return (
      <>
        <div className="d-flex justify-content-between">
          <span>{t("events.total")}</span>
          <span className="fw-bold">{fmt(0)}</span>
        </div>
        <p className="small text-muted mb-0 mt-2">
          {t("events.complimentaryTicketNotice")}
        </p>
      </>
    );
  }

  const showMemberDiscount =
    memberDiscountEligible &&
    (membershipName === MEMBERSHIP_BUSINESS ||
      membershipName === MEMBERSHIP_EXECUTIVE);

  const baseBeforeMember =
    ticketSubtotalAfterPromoForMemberLine ?? subtotalBeforeMemberDiscount;

  const memberDiscountAmount = showMemberDiscount
    ? (parseFloat(baseBeforeMember) - parseFloat(finalPrice)).toFixed(2)
    : null;

  return (
    <>
      <div className="d-flex justify-content-between">
        <span>{t("events.price")}</span>
        <span className="fw-bold">{fmt(data?.price)}</span>
      </div>
      {isEarlyBird && (
        <div className="d-flex justify-content-between">
          <span>{t("events.earlyBirdDiscount")}</span>
          <span className="fw-bold text-success">
            -{fmt(data?.earlyBirdPrice ?? 0)}
          </span>
        </div>
      )}
      {showMemberDiscount && memberDiscountAmount !== null && (
        <div className="d-flex justify-content-between">
          <span>
            {membershipName === MEMBERSHIP_BUSINESS
              ? t("events.businessMemberDiscount")
              : t("events.executiveMemberDiscount")}
          </span>
          <span className="fw-bold text-success text-nowrap">
            -{fmt(parseFloat(memberDiscountAmount))}
          </span>
        </div>
      )}
      {eventCouponDiscountAmount != null && eventCouponDiscountAmount > 0 && (
        <div className="d-flex justify-content-between">
          <span>{t("events.eventPromotion")}</span>
          <span className="fw-bold text-success text-nowrap">
            -{fmt(eventCouponDiscountAmount)}
          </span>
        </div>
      )}
      <div className="d-flex justify-content-between">
        <span>{t("events.total")}</span>
        <span className="fw-bold">{fmt(parseFloat(finalPrice))}</span>
      </div>
    </>
  );
});
PriceSummary.displayName = "PriceSummary";

interface PaymentMethodChoiceProps {
  paymentMethods: { last4?: string; brand?: string }[] | undefined;
  useSavedMethod: boolean;
  onUseSaved: () => void;
  onUseNew: () => void;
}

const PaymentMethodChoice = memo(function PaymentMethodChoice({
  paymentMethods,
  useSavedMethod,
  onUseSaved,
  onUseNew,
}: PaymentMethodChoiceProps) {
  const { t } = useTranslation();
  if (!paymentMethods?.length) return null;
  return (
    <div className="mb-3">
      <label className="form-label">{t("events.choosePaymentMethod")}</label>
      <div className="form-check mb-2">
        <input
          type="radio"
          className="form-check-input"
          id="savedCard"
          checked={useSavedMethod}
          onChange={onUseSaved}
          aria-label={t("events.useSavedCardAria")}
        />
        <label className="form-check-label" htmlFor="savedCard">
          {t("events.useSavedCard")} (**** **** **** {paymentMethods[0].last4},{" "}
          {paymentMethods[0].brand})
        </label>
      </div>
      <div className="form-check">
        <input
          type="radio"
          className="form-check-input"
          id="newCard"
          checked={!useSavedMethod}
          onChange={onUseNew}
          aria-label={t("events.useDifferentCardAria")}
        />
        <label className="form-check-label" htmlFor="newCard">
          {t("events.useDifferentCard")}
        </label>
      </div>
    </div>
  );
});
PaymentMethodChoice.displayName = "PaymentMethodChoice";

function ConfirmBooking() {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const { objectId } = useAppContext();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [billingAddress, setBillingAddress] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [useSavedMethod, setUseSavedMethod] = useState(false);
  const bookingEmailSentRef = useRef(false);
  const submitLockRef = useRef(false);
  const checkoutSessionHandledRef = useRef(false);
  const paymentRedirectHandledRef = useRef(false);
  const eventBookingCompletedRef = useRef(false);
  const navigate = useNavigate();
  const { mutate, mutateAsync: payEventAsync } = useMakePayment();
  const queryClient = useQueryClient();
  const { user } = useAppContext();

  const onBookSuccess = useCallback(
    (data: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["booked-events", objectId] });
      queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      localStorage.removeItem(EVENT_DATA_KEY);
    },
    [queryClient, objectId],
  );
  const { mutate: bookEvent } = useBookEvent({ onSuccess: onBookSuccess });

  useEffect(() => {
    eventBookingCompletedRef.current = false;
    paymentRedirectHandledRef.current = false;
    checkoutSessionHandledRef.current = false;
    bookingEmailSentRef.current = false;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [eventId]);

  const { mutate: sendBookingConfirmationMail } =
    useSendEventBookingConfirmation();
  const { data: paymentMethods } = useGetBilling({ userId: objectId || "" });
  const savedCards = useMemo(() => getSavedCards(paymentMethods), [paymentMethods]);
  const { data, isError: isEventLoadError } = useGetEvent({ eventId: eventId || "" });
  const { data: memberships } = useGetMemberships();
  const membershipAccessPending = useMemo(
    () =>
      isMembershipAccessCheckPending(
        data?.allowedMembershipIds,
        user?.user?.membershipName,
        memberships,
        user?.user?.hasPaid,
        user?.user?.stripeStatus,
      ),
    [
      data?.allowedMembershipIds,
      user?.user?.membershipName,
      memberships,
      user?.user?.hasPaid,
      user?.user?.stripeStatus,
    ],
  );

  const canAccessEvent = useMemo(() => {
    if (!data || membershipAccessPending) return true;
    return canUserViewEvent(data.allowedMembershipIds, user?.user?.membershipId, {
      membershipName: user?.user?.membershipName,
      memberships,
      hasPaid: user?.user?.hasPaid,
      stripeStatus: user?.user?.stripeStatus,
    });
  }, [
    data,
    membershipAccessPending,
    user?.user?.membershipId,
    user?.user?.membershipName,
    user?.user?.hasPaid,
    user?.user?.stripeStatus,
    memberships,
  ]);

  useEffect(() => {
    if (isEventLoadError) {
      navigate(BACK_ROUTE, { replace: true });
    }
  }, [isEventLoadError, navigate]);

  useEffect(() => {
    void ensureExplorerProfileSyncedForEventBooking().then((saved) => {
      if (saved && objectId) {
        queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      }
    });
  }, [objectId, queryClient]);

  const { showApplePay, showGooglePay } = getWalletButtonVisibility();

  const isBusinessOrExecutive = useMemo(
    () => userQualifiesForMemberEventDiscount(user?.user),
    [user?.user],
  );

  const totalPrice = useMemo(() => {
    if (
      data?.price &&
      data?.earlyBirdPrice &&
      data?.earlyBirdDateEnd &&
      new Date(data.earlyBirdDateEnd) > new Date()
    ) {
      return (data.price - data.earlyBirdPrice).toFixed(2);
    }
    return data?.price ? data.price.toFixed(2) : "0.00";
  }, [data?.price, data?.earlyBirdPrice, data?.earlyBirdDateEnd]);

  const isEarlyBird = useMemo(
    () =>
      !!data?.earlyBirdDateEnd && new Date(data.earlyBirdDateEnd) > new Date(),
    [data?.earlyBirdDateEnd],
  );

  const [eventDiscountCode, setEventDiscountCode] = useState("");
  const [showEventDiscountInput, setShowEventDiscountInput] = useState(false);
  const [eventDiscountPreview, setEventDiscountPreview] =
    useState<PreviewEventStripeDiscountResponse | null>(null);
  /** True once server pricing preview finished (or not needed for free tickets). */
  const [pricingPreviewSettled, setPricingPreviewSettled] = useState(false);
  const { mutateAsync: previewStripeEventDiscount } =
    usePreviewEventStripeDiscount();

  const freeForGroupsResolved =
    data?.freeForGroups ?? eventDiscountPreview?.freeForGroups ?? null;

  const adminFreeForUser = useMemo(
    () => isEventAdminFreeForUser(freeForGroupsResolved, user?.user),
    [freeForGroupsResolved, user?.user],
  );

  const memberDiscountEligible = useMemo(
    () =>
      eventEligibleForMemberTierDiscount(
        eventDiscountPreview?.memberEventDiscountEligible ??
          data?.memberEventDiscountEligible,
      ),
    [
      eventDiscountPreview?.memberEventDiscountEligible,
      data?.memberEventDiscountEligible,
    ],
  );

  const finalPrice = useMemo(() => {
    if (adminFreeForUser) {
      return "0.00";
    }
    if (!isBusinessOrExecutive || !memberDiscountEligible) return totalPrice;
    if (user?.user?.membershipName === MEMBERSHIP_BUSINESS) {
      return (parseFloat(totalPrice) / 2).toFixed(2);
    }
    return totalPrice;
  }, [
    adminFreeForUser,
    isBusinessOrExecutive,
    memberDiscountEligible,
    totalPrice,
    user?.user,
  ]);

  // Backend preview includes Executive "first event in month free" + promos; prefer it whenever valid so UI matches charge.
  const displayFinalPrice = useMemo(() => {
    if (adminFreeForUser) return "0.00";
    if (
      eventDiscountPreview?.valid &&
      eventDiscountPreview.finalTotal != null
    ) {
      return eventDiscountPreview.finalTotal.toFixed(2);
    }
    return finalPrice;
  }, [adminFreeForUser, eventDiscountPreview, finalPrice]);
  const isZeroAmountDue = useMemo(
    () => adminFreeForUser || parseFloat(displayFinalPrice) <= 0,
    [adminFreeForUser, displayFinalPrice],
  );

  /** Same booking style as Executive complimentary — no invoice discount lines / card prompts. */
  const isComplimentaryTicket = isZeroAmountDue;

  const needsServerPricingPreview =
    !adminFreeForUser && (data?.price ?? 0) > 0;

  const eventPromoDiscountForSummary = useMemo(() => {
    if (isComplimentaryTicket) return null;
    const trimmed = eventDiscountCode.trim();
    if (
      !trimmed ||
      !eventDiscountPreview?.valid ||
      eventDiscountPreview.couponDiscountAmount == null ||
      eventDiscountPreview.couponDiscountAmount <= 0
    )
      return null;
    return eventDiscountPreview.couponDiscountAmount;
  }, [isComplimentaryTicket, eventDiscountCode, eventDiscountPreview]);

  const ticketSubtotalAfterPromoForMemberLine = useMemo(() => {
    if (isComplimentaryTicket) return null;
    if (
      !eventDiscountCode.trim() ||
      !eventDiscountPreview?.valid ||
      eventDiscountPreview.subtotalAfterCoupon == null
    )
      return null;
    return eventDiscountPreview.subtotalAfterCoupon.toFixed(2);
  }, [isComplimentaryTicket, eventDiscountCode, eventDiscountPreview]);

  /** Promo code entry — hide once the ticket is complimentary (£0). */
  const showEventStripePromoUi = useMemo(
    () => !isComplimentaryTicket && (data?.price ?? 0) > 0,
    [isComplimentaryTicket, data?.price],
  );

  useEffect(() => {
    if (!needsServerPricingPreview) {
      setPricingPreviewSettled(true);
      return;
    }
    if (!objectId || !eventId) {
      setPricingPreviewSettled(false);
      return;
    }
    setPricingPreviewSettled(false);
  }, [
    needsServerPricingPreview,
    objectId,
    eventId,
    isEarlyBird,
    eventDiscountCode,
  ]);

  // Always load server pricing when needed (Executive first-free-in-month lives here).
  // Do not gate on showEventStripePromoUi — once finalTotal is £0 that flag goes false and
  // clearing the preview would snap the UI back to full price.
  useEffect(() => {
    if (!needsServerPricingPreview || !objectId || !eventId) {
      if (!needsServerPricingPreview) {
        setEventDiscountPreview(null);
      }
      return;
    }
    const code = eventDiscountCode.trim();
    const tid = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await previewStripeEventDiscount({
            userId: objectId,
            eventId: parseInt(eventId, 10),
            discountCode: code,
            isEventEarlyBird: isEarlyBird,
          });
          setEventDiscountPreview(res);
        } catch {
          setEventDiscountPreview({
            valid: false,
            error: t("events.couldNotVerifyPromo"),
          });
        } finally {
          setPricingPreviewSettled(true);
        }
      })();
    }, 480);
    return () => window.clearTimeout(tid);
  }, [
    eventDiscountCode,
    needsServerPricingPreview,
    objectId,
    eventId,
    isEarlyBird,
    previewStripeEventDiscount,
    t,
  ]);

  const sendBookingEmail = useCallback(() => {
    if (bookingEmailSentRef.current) return;
    const toEmail = user?.user?.email ?? "";
    if (!toEmail.trim()) return;
    const fullName =
      `${user?.user?.firstName ?? ""} ${user?.user?.lastName ?? ""}`.trim() ||
      "Member";
    const eventName = data?.name || "SMBF Event";
    const eventDateText = data?.eventDate
      ? new Date(data.eventDate).toLocaleString()
      : "TBC";

    bookingEmailSentRef.current = true;
    const parsedEventId = eventId ? parseInt(eventId, 10) : NaN;
    sendBookingConfirmationMail({
      ...(Number.isFinite(parsedEventId) && parsedEventId > 0
        ? { eventId: parsedEventId }
        : {}),
      email: toEmail,
      name: fullName,
      firstName: user?.user?.firstName ?? undefined,
      eventName,
      eventDateText,
    });
  }, [
    eventId,
    user?.user?.email,
    user?.user?.firstName,
    user?.user?.lastName,
    data?.name,
    data?.eventDate,
    sendBookingConfirmationMail,
  ]);

  const finalizeEventRegistration = useCallback(async () => {
    if (eventBookingCompletedRef.current) return false;
    eventBookingCompletedRef.current = true;

    try {
      await ensureExplorerProfileSyncedForEventBooking();
    } catch {
      /* best-effort before AddEventAttendee merge */
    }

    const formData = await resolveEventBookingFormData();
    const sectors = formData.sectorsOfInterest;
    const promoUsed =
      (eventId && sessionStorage.getItem(`eventBookingPromo:${eventId}`)) || "";
    if (eventId) {
      sessionStorage.removeItem(`eventBookingPromo:${eventId}`);
    }
    bookEvent({
      stageOfBusiness: formData.stageOfBusiness ?? "",
      eventObjectives: formData.objectives ?? "",
      sectorsOfInterest: Array.isArray(sectors) ? sectors.join(", ") : "",
      consentToShareProfile: formData.consentToShare ? 1 : 0,
      eventId: eventId ? parseInt(eventId, 10) : 0,
      userId: objectId || "",
      appliedEventDiscountCode: promoUsed || undefined,
    });
    sendBookingEmail();
    setShowSuccessModal(true);
    return true;
  }, [eventId, objectId, bookEvent, sendBookingEmail]);

  const completeZeroAmountBooking = useCallback(async () => {
    await finalizeEventRegistration();
    setLoading(false);
    submitLockRef.current = false;
  }, [finalizeEventRegistration]);

  useEffect(() => {
    if (savedCards.length > 0) {
      setUseSavedMethod(true);
    }
  }, [savedCards.length]);

  // Handle return from 3DS redirect: complete booking when user lands back with success
  useEffect(() => {
    if (!stripe || !eventId || !objectId) return;

    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get("payment_intent_client_secret");
    const redirectStatus = params.get("redirect_status");

    if (clientSecret && redirectStatus === "succeeded") {
      if (paymentRedirectHandledRef.current) return;
      paymentRedirectHandledRef.current = true;
      stripe
        .retrievePaymentIntent(clientSecret)
        .then(async ({ paymentIntent }) => {
          if (paymentIntent?.status === "succeeded") {
            await finalizeEventRegistration();
            window.history.replaceState({}, "", window.location.pathname);
          }
        })
        .catch(() => {
          paymentRedirectHandledRef.current = false;
        });
    } else if (clientSecret && redirectStatus === "failed") {
      if (paymentRedirectHandledRef.current) return;
      paymentRedirectHandledRef.current = true;
      const msg = t("events.authenticationFailed");
      logPaymentError({
        flow: "event-booking",
        errorSource: "stripe",
        message: msg,
        error: { redirect_status: "failed" },
      });
      setError(msg);
      setLoading(false);
      submitLockRef.current = false;
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [stripe, eventId, objectId, finalizeEventRegistration, t]);

  /** Return from Stripe Checkout: verify session server-side, then complete booking (same as paid card flow). */
  useEffect(() => {
    if (!objectId || !eventId) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout_cancel") === "1") {
      window.history.replaceState({}, "", window.location.pathname);
      setError(t("events.paymentCancelled"));
      return;
    }

    const sessionId = params.get("session_id");
    if (!sessionId || checkoutSessionHandledRef.current) return;

    checkoutSessionHandledRef.current = true;
    setLoading(true);
    setError("");

    axiosInstance
      .post(getAPIURL("/Payment/verify-checkout-session"), { sessionId })
      .then(async () => {
        await finalizeEventRegistration();
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((err: unknown) => {
        checkoutSessionHandledRef.current = false;
        const responseData =
          err &&
          typeof err === "object" &&
          "response" in err &&
          (err as { response?: { data?: { error?: string } } }).response?.data;
        const msg =
          responseData && typeof responseData === "object" && "error" in responseData
            ? String((responseData as { error: string }).error)
            : t("events.couldNotConfirmPayment");
        logPaymentError({
          flow: "event-booking",
          errorSource: "backend",
          message: msg,
          error: err,
        });
        setError(msg);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [objectId, eventId, finalizeEventRegistration]);

  const handleBack = useCallback(() => {
    navigate(BACK_ROUTE);
  }, [navigate]);

  const handleHideSuccess = useCallback(() => setShowSuccessModal(false), []);

  const stripeElementOptions = useMemo(
    () => ({ style: STRIPE_ELEMENT_STYLE }),
    [],
  );

  const handleUseSavedMethod = useCallback(() => {
    setUseSavedMethod(true);
  }, []);

  const handleUseNewMethod = useCallback(() => {
    setUseSavedMethod(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (submitLockRef.current) return;
      if (!isZeroAmountDue && (!stripe || !elements)) {
        const msg =
          t("events.paymentFormLoading");
        logPaymentError({
          flow: "event-booking",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        return;
      }

      submitLockRef.current = true;
      setLoading(true);
      setError("");

      try {
        if (!isZeroAmountDue && (!stripe || !elements)) {
          const msg =
            t("events.paymentFormLoading");
          logPaymentError({
            flow: "event-booking",
            errorSource: "frontend",
            message: msg,
          });
          setError(msg);
          setLoading(false);
          submitLockRef.current = false;
          return;
        }

        const promoTrim = eventDiscountCode.trim();
        if (promoTrim && !eventDiscountPreview?.valid) {
          const msg =
            t("events.enterValidPromoOrClear");
          setError(msg);
          setLoading(false);
          submitLockRef.current = false;
          return;
        }

        if (eventId) {
          sessionStorage.setItem(`eventBookingPromo:${eventId}`, promoTrim);
        }

        if (needsServerPricingPreview && !pricingPreviewSettled) {
          setError(t("events.priceStillLoading"));
          setLoading(false);
          submitLockRef.current = false;
          return;
        }

        if (isZeroAmountDue) {
          await completeZeroAmountBooking();
          return;
        }

        if (!stripe || !elements) {
          const msg =
            t("events.paymentFormLoading");
          logPaymentError({
            flow: "event-booking",
            errorSource: "frontend",
            message: msg,
          });
          setError(msg);
          setLoading(false);
          submitLockRef.current = false;
          return;
        }

        const cardNumberElement = elements?.getElement(CardNumberElement);

        let newPaymentMethod;
        if (!useSavedMethod && cardNumberElement) {
          const { paymentMethod, error: pmError } =
            await stripe.createPaymentMethod({
              type: "card",
              card: cardNumberElement,
            });
          if (pmError || !paymentMethod) {
            const msg = pmError?.message || t("events.cardCreationFailed");
            logPaymentError({
              flow: "event-booking",
              errorSource: "stripe",
              message: msg,
              error: pmError,
            });
            setError(msg);
            setLoading(false);
            submitLockRef.current = false;
            return;
          }
          newPaymentMethod = paymentMethod;
        }

        const selectedPaymentMethodId =
          useSavedMethod && savedCards[0]?.paymentMethodId
            ? savedCards[0].paymentMethodId
            : newPaymentMethod?.id;

        if (!selectedPaymentMethodId) {
          const msg = t("events.noValidPaymentMethod");
          logPaymentError({
            flow: "event-booking",
            errorSource: "frontend",
            message: msg,
          });
          setError(msg);
          setLoading(false);
          submitLockRef.current = false;
          return;
        }

        mutate(
          {
            paymentMethodTypes: ["card"],
            customerId: "",
            paymentMethodId: selectedPaymentMethodId,
            userId: objectId || "",
            name: `${user?.user?.firstName || ""} ${
              user?.user?.lastName || ""
            }`.trim(),
            email: user?.user?.email || "",
            productId: data?.productId,
            discountCode: promoTrim,
            IsEventEarlyBird: isEarlyBird,
            isEvent: true,
          },
          {
            onSuccess: async (response) => {
              const clientSecret = response.clientSecret;
              const skipStripeConfirm =
                response.noPaymentRequired || response.paymentAlreadySucceeded;

              if (!skipStripeConfirm && !clientSecret) {
                const msg = t("events.noClientSecret");
                logPaymentError({
                  flow: "event-booking",
                  errorSource: "backend",
                  message: msg,
                  responseData: response,
                });
                setError(msg);
                setLoading(false);
                submitLockRef.current = false;
                return;
              }

              // Zero due or payment already captured on server — skip confirmCardPayment
              if (skipStripeConfirm) {
                await completeZeroAmountBooking();
                return;
              }

              const paymentClientSecret = clientSecret;
              if (!paymentClientSecret) {
                const msg = t("events.noClientSecret");
                logPaymentError({
                  flow: "event-booking",
                  errorSource: "backend",
                  message: msg,
                  responseData: response,
                });
                setError(msg);
                setLoading(false);
                submitLockRef.current = false;
                return;
              }

              const returnUrl = `${window.location.origin}${window.location.pathname}`;
              const { paymentIntent, error: confirmError } =
                await stripe.confirmCardPayment(paymentClientSecret, {
                  payment_method: selectedPaymentMethodId,
                  return_url: returnUrl,
                });

              if (confirmError) {
                const confirmMsg =
                  confirmError.message || t("events.paymentConfirmationFailed");
                logPaymentError({
                  flow: "event-booking",
                  errorSource: "stripe",
                  message: confirmMsg,
                  error: confirmError,
                });
                // Payment already succeeded (e.g. double submit or back-button) - treat as success
                if (confirmError.code === "payment_intent_unexpected_state") {
                  await finalizeEventRegistration();
                } else {
                  const isAuthFailure =
                    confirmError.code ===
                    "payment_intent_authentication_failure";

                  const msg = isAuthFailure
                    ? t("events.authenticationFailed")
                    : confirmError.message || t("events.paymentConfirmationFailed");

                  setError(msg);
                }

                setLoading(false);
                submitLockRef.current = false;
                return;
              }
              if (paymentIntent && paymentIntent.status === "succeeded") {
                await finalizeEventRegistration();
              }
              setLoading(false);
              submitLockRef.current = false;
            },
            onError: (err: unknown) => {
              if (eventId) {
                sessionStorage.removeItem(`eventBookingPromo:${eventId}`);
              }
              const source = getPaymentErrorSource(err);
              const msg = getPaymentErrorMessage(err);
              const responseStatus =
                err &&
                typeof err === "object" &&
                "response" in err
                  ? (err as { response?: { status?: number } }).response?.status
                  : undefined;
              const responseData =
                err &&
                typeof err === "object" &&
                "response" in err
                  ? (err as { response?: { data?: unknown } }).response?.data
                  : undefined;
              logPaymentError({
                flow: "event-booking",
                errorSource: source,
                message: msg,
                error: err,
                responseStatus,
                responseData,
              });
              setError(msg);
              setLoading(false);
              submitLockRef.current = false;
            },
          },
        );
      } catch (err: unknown) {
        if (eventId) {
          sessionStorage.removeItem(`eventBookingPromo:${eventId}`);
        }
        const msg =
          err instanceof Error ? err.message : t("events.unexpectedError");
        logPaymentError({
          flow: "event-booking",
          errorSource: "frontend",
          message: msg,
          error: err,
        });
        setError(msg);
        setLoading(false);
        submitLockRef.current = false;
      }
    },
    [
      stripe,
      elements,
      useSavedMethod,
      savedCards,
      mutate,
      objectId,
      user,
      data,
      eventId,
      finalizeEventRegistration,
      isEarlyBird,
      eventDiscountCode,
      eventDiscountPreview,
      isZeroAmountDue,
      needsServerPricingPreview,
      pricingPreviewSettled,
      completeZeroAmountBooking,
      t,
    ],
  );

  const completeEventPaymentFromIntent = useCallback(
    async (
      response: CreatePaymentIntentResponse,
      paymentMethodId: string,
      options?: { walletFlow?: boolean },
    ) => {
      const walletFlow = Boolean(options?.walletFlow);
      const clientSecret = response.clientSecret;
      const skipStripeConfirm =
        response.noPaymentRequired || response.paymentAlreadySucceeded;

      if (!skipStripeConfirm && !clientSecret) {
        const msg = t("events.noClientSecret");
        logPaymentError({
          flow: "event-booking",
          errorSource: "backend",
          message: msg,
          responseData: response,
        });
        setError(msg);
        setLoading(false);
        if (!walletFlow) submitLockRef.current = false;
        return;
      }

      if (skipStripeConfirm) {
        await completeZeroAmountBooking();
        if (!walletFlow) submitLockRef.current = false;
        return;
      }

      const paymentClientSecret = clientSecret;
      if (!paymentClientSecret) {
        const msg = t("events.noClientSecret");
        logPaymentError({
          flow: "event-booking",
          errorSource: "backend",
          message: msg,
          responseData: response,
        });
        setError(msg);
        setLoading(false);
        if (!walletFlow) submitLockRef.current = false;
        return;
      }

      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const { paymentIntent, error: confirmError } =
        await stripe!.confirmCardPayment(paymentClientSecret, {
          payment_method: paymentMethodId,
          return_url: returnUrl,
        });

      if (confirmError) {
        const confirmMsg =
          confirmError.message || t("events.paymentConfirmationFailed");
        logPaymentError({
          flow: "event-booking",
          errorSource: "stripe",
          message: confirmMsg,
          error: confirmError,
        });
        if (confirmError.code === "payment_intent_unexpected_state") {
          await finalizeEventRegistration();
        } else {
          const isAuthFailure =
            confirmError.code === "payment_intent_authentication_failure";

          const msg = isAuthFailure
            ? t("events.authenticationFailed")
            : confirmError.message || t("events.paymentConfirmationFailed");

          setError(msg);
        }

        setLoading(false);
        if (!walletFlow) submitLockRef.current = false;
        return;
      }
      if (paymentIntent && paymentIntent.status === "succeeded") {
        await finalizeEventRegistration();
      }
      setLoading(false);
      if (!walletFlow) submitLockRef.current = false;
    },
    [stripe, finalizeEventRegistration, completeZeroAmountBooking, t],
  );

  const handleStripeHostedCheckout = useCallback(
    async (preferredWallet?: "apple_pay" | "google_pay") => {
      if (!preferredWallet) return;

      const walletMessage = getUnsupportedWalletMessage(preferredWallet);
      if (walletMessage) {
        setError(walletMessage);
        return;
      }

      if (!objectId || !eventId || !data?.productId) return;

      if (needsServerPricingPreview && !pricingPreviewSettled) {
        setError(t("events.priceStillLoading"));
        return;
      }

      if (isZeroAmountDue) {
        if (eventId) {
          sessionStorage.setItem(
            `eventBookingPromo:${eventId}`,
            eventDiscountCode.trim(),
          );
        }
        setLoading(true);
        setError("");
        submitLockRef.current = true;
        try {
          await completeZeroAmountBooking();
        } finally {
          submitLockRef.current = false;
        }
        return;
      }

      if (!stripe) return;

      const promoTrim = eventDiscountCode.trim();
      if (promoTrim && !eventDiscountPreview?.valid) {
        setError(
          t("events.enterValidPromoOrClear"),
        );
        return;
      }

      sessionStorage.setItem(`eventBookingPromo:${eventId}`, promoTrim);
      setLoading(true);
      setError("");

      try {
        const preview = await previewWalletPricing({
          userId: objectId,
          productId: data.productId,
          discountCode: promoTrim || undefined,
          isAnnual: false,
          isEvent: true,
          isEventEarlyBird: isEarlyBird,
          name: `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim(),
          email: user?.user?.email || "",
        });

        if (preview.noPaymentRequired) {
          await completeEventPaymentFromIntent(
            { noPaymentRequired: true, clientSecret: undefined },
            "",
            { walletFlow: true },
          );
          return;
        }

        await runWalletPaymentRequest({
          stripe,
          amountMinor: preview.amountMinor,
          currency: preview.currency,
          label: preview.label,
          preferredWallet,
          onPaymentMethod: async (pm) => {
            const response = await payEventAsync({
              paymentMethodTypes: ["card"],
              customerId: "",
              paymentMethodId: pm.id,
              userId: objectId || "",
              name: `${user?.user?.firstName || ""} ${
                user?.user?.lastName || ""
              }`.trim(),
              email: user?.user?.email || "",
              productId: data.productId,
              discountCode: promoTrim,
              IsEventEarlyBird: isEarlyBird,
              isEvent: true,
            });
            await completeEventPaymentFromIntent(response, pm.id, {
              walletFlow: true,
            });
          },
        });
      } catch (err: unknown) {
        const msg = getPaymentErrorMessage(err);
        if (err instanceof Error && err.message === "cancelled") {
          setError("");
        } else {
          const source = getPaymentErrorSource(err);
          logPaymentError({
            flow: "event-booking",
            errorSource: source,
            message: msg,
            error: err,
          });
          setError(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      objectId,
      eventId,
      data?.productId,
      stripe,
      eventDiscountCode,
      eventDiscountPreview?.valid,
      isEarlyBird,
      user?.user?.firstName,
      user?.user?.lastName,
      user?.user?.email,
      payEventAsync,
      completeEventPaymentFromIntent,
      isZeroAmountDue,
      needsServerPricingPreview,
      pricingPreviewSettled,
      completeZeroAmountBooking,
      t,
    ],
  );
  if ((!canAccessEvent && !membershipAccessPending) || isEventLoadError) {
    return (
      <div className="m-4 rounded-4 p-4 shadow border-0">
        <p className="text-muted mb-0">{t("events.membershipNotAvailable")}</p>
      </div>
    );
  }

  return (
    <div className="m-4 rounded-4 p-4 shadow border-0 d-flex flex-column">
      <form onSubmit={handleSubmit} className="w-100" autoComplete="off">
        <div className="d-flex flex-column gap-3">
          <div className="d-flex justify-content-between">
            <h5>{t("events.checkout")}</h5>
            <h5>{t("events.eventBooking")}</h5>
          </div>

          <PriceSummary
            data={data}
            isEarlyBird={isEarlyBird && !isComplimentaryTicket}
            finalPrice={displayFinalPrice}
            membershipName={
              !isComplimentaryTicket &&
              userQualifiesForMemberEventDiscount(user?.user)
                ? user?.user?.membershipName
                : undefined
            }
            subtotalBeforeMemberDiscount={totalPrice}
            currencyCode={data?.currency}
            eventCouponDiscountAmount={eventPromoDiscountForSummary}
            ticketSubtotalAfterPromoForMemberLine={
              ticketSubtotalAfterPromoForMemberLine
            }
            memberDiscountEligible={memberDiscountEligible}
            isComplimentaryTicket={isComplimentaryTicket}
          />

          {!isComplimentaryTicket && (
            <p className="small text-muted mb-2">
              {t("events.payWithCardOnPage")}
            </p>
          )}

          {!isZeroAmountDue && (
            <PaymentMethodChoice
              paymentMethods={savedCards}
              useSavedMethod={useSavedMethod}
              onUseSaved={handleUseSavedMethod}
              onUseNew={handleUseNewMethod}
            />
          )}

          {!isZeroAmountDue && !useSavedMethod && (
            <div className="row g-2">
              <div className="col-md-6 mb-3">
                <label>{t("events.cardNumber")}</label>
                <div className="form-control">
                  <CardNumberElement options={stripeElementOptions} />
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <label>{t("events.expiry")}</label>
                <div className="form-control">
                  <CardExpiryElement options={stripeElementOptions} />
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <label>{t("events.cvc")}</label>
                <div className="form-control">
                  <CardCvcElement options={stripeElementOptions} />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label>{t("events.billingAddressOptional")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("events.billingAddress")}
                  value={billingAddress}
                  autoComplete="new-address"
                  onChange={(e) => setBillingAddress(e.target.value)}
                />
              </div>
            </div>
          )}

          {showEventStripePromoUi && (
            <div className="mt-1">
              {!showEventDiscountInput ? (
                <button
                  type="button"
                  className="btn btn-link p-0 text-start border-0 bg-transparent text-primary text-decoration-underline"
                  onClick={() => setShowEventDiscountInput(true)}
                >
                  {t("events.havePromotionCode")}
                </button>
              ) : (
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">
                    {t("events.eventPromotionCode")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    value={eventDiscountCode}
                    onChange={(e) => setEventDiscountCode(e.target.value)}
                    placeholder={t("events.promotionCodePlaceholder")}
                  />
                  {eventDiscountCode.trim().length > 0 &&
                    eventDiscountPreview &&
                    eventDiscountPreview.valid === false &&
                    eventDiscountPreview.error && (
                      <Form.Text className="text-danger d-block">
                        {eventDiscountPreview.error}
                      </Form.Text>
                    )}
                </Form.Group>
              )}
            </div>
          )}

          <hr />

          {!isZeroAmountDue && (
          <div className="rounded-3 p-2 bg-white">
            <div className="d-flex align-items-center justify-content-start gap-3">
              {showApplePay && (
              <button
                type="button"
                className="btn btn-dark border border-secondary text-white fw-semibold d-flex align-items-center justify-content-center flex-fill rounded-4 px-4 py-3 shadow-sm"
                disabled={
                  loading ||
                  !data?.productId ||
                  !objectId ||
                  (needsServerPricingPreview && !pricingPreviewSettled)
                }
                onClick={() => void handleStripeHostedCheckout("apple_pay")}
              >
                <span className="d-inline-flex align-items-center justify-content-center gap-2">
                  <span className="d-inline-flex align-items-center justify-content-center gap-1">
                    <FontAwesomeIcon
                      icon={faApple}
                      className="fs-4"
                    />
                    <span className="fs-3 fw-medium lh-1 text-white">
                      {t("events.walletPay")}
                    </span>
                  </span>
                </span>
              </button>
              )}
              {showGooglePay && (
              <button
                type="button"
                className="btn btn-light bg-white border border-secondary-subtle fw-semibold d-flex align-items-center justify-content-center flex-fill rounded-4 px-4 py-3 shadow-sm"
                disabled={
                  loading ||
                  !data?.productId ||
                  !objectId ||
                  (needsServerPricingPreview && !pricingPreviewSettled)
                }
                onClick={() => void handleStripeHostedCheckout("google_pay")}
              >
                <span className="d-inline-flex align-items-center justify-content-center gap-2">
                  <span className="d-inline-flex align-items-center justify-content-center gap-1">
                    <FcGoogle className="fs-4" />
                    <span className="fs-3 fw-medium lh-1 text-dark">
                      {t("events.walletPay")}
                    </span>
                  </span>
                </span>
              </button>
              )}
            </div>
          </div>
          )}

          {error && (
            <div className="alert alert-danger py-2 px-3" role="alert">
              {error}
            </div>
          )}

          <div className="d-flex justify-content-between mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleBack}
            >
              {t("auth.back")}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                loading ||
                (needsServerPricingPreview && !pricingPreviewSettled) ||
                (!isZeroAmountDue && !stripe)
              }
            >
              {loading
                ? t("events.processing")
                : needsServerPricingPreview && !pricingPreviewSettled
                  ? t("events.loadingPrice")
                  : isZeroAmountDue
                    ? t("events.bookEvent")
                    : t("events.payNow")}
            </button>
          </div>
        </div>
      </form>
      {showSuccessModal && (
        <Suspense fallback={null}>
          <EventSuccessModal
            show={showSuccessModal}
            onHide={handleHideSuccess}
            text={
              isZeroAmountDue
                ? t("events.bookingConfirmed")
                : t("events.paymentSuccessBookingConfirmed")
            }
            meetingLink={data?.isOnline ? data?.meetingLink : null}
          />
        </Suspense>
      )}
    </div>
  );
}

ConfirmBooking.displayName = "ConfirmBooking";

export default memo(ConfirmBooking);
